import React, { useState } from "react";
import { Save, ShoppingBag, Eye, EyeOff, DollarSign, Film, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function VideoStoreSettings({ video, onUpdate }) {
  const [storeData, setStoreData] = useState({
    store_enabled: video?.store_enabled || false,
    store_sale_type: video?.store_sale_type || "both",
    store_direct_sale_price: video?.store_direct_sale_price || 19.99,
    store_ppv_price: video?.store_ppv_price || 4.99,
    store_ppv_duration_hours: video?.store_ppv_duration_hours || 48,
    store_description: video?.store_description || "",
    thumbnail_url: video?.thumbnail_url || "",
    trailer_url: video?.trailer_url || "",
  });
  
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const handleSave = async () => {
    if (!video?.id) return;
    setSaving(true);
    try {
      await api.put(`/api/videos/${video.id}`, storeData);
      toast.success("✓ Store settings saved");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleThumbnailUpload = async (file) => {
    if (!file) return;
    
    setUploadingThumbnail(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/api/landing-config/upload/ad-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setStoreData({...storeData, thumbnail_url: response.data.url});
      toast.success("✓ Thumbnail uploaded!");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const linkTrailerFromMux = () => {
    // SECURITY: Only link the TRAILER Mux video, NEVER the full video
    if (video?.trailer_mux_playback_id) {
      setStoreData({...storeData, trailer_url: `https://stream.mux.com/${video.trailer_mux_playback_id}.m3u8`});
      toast.success("✓ Linked trailer Mux video");
    } else if (video?.mux_playback_id) {
      // CRITICAL WARNING: Don't auto-link full video
      toast.error("⚠️ No trailer uploaded! Cannot use full video as trailer. Please upload a dedicated trailer in the Mux tab first.");
    } else {
      toast.error("No Mux video available");
    }
  };

  return (
    <div className="space-y-4">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: "var(--gio-surface-hi)", border: "1px solid var(--gio-border)" }}>
        <div className="flex items-center gap-3">
          <ShoppingBag size={24} style={{ color: storeData.store_enabled ? "#FFD700" : "var(--gio-muted)" }} />
          <div>
            <div className="font-semibold" style={{ color: "var(--gio-text)" }}>
              {storeData.store_enabled ? "Listed in Store" : "Not in Store"}
            </div>
            <div className="text-sm" style={{ color: "var(--gio-muted)" }}>
              {storeData.store_enabled ? "Customers can purchase this video" : "Hidden from public store"}
            </div>
          </div>
        </div>
        <button
          onClick={() => setStoreData({...storeData, store_enabled: !storeData.store_enabled})}
          className={`px-4 py-2 rounded-lg font-medium transition ${storeData.store_enabled ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"}`}
        >
          {storeData.store_enabled ? <><Eye size={16} className="inline mr-2" />Enabled</> : <><EyeOff size={16} className="inline mr-2" />Disabled</>}
        </button>
      </div>

      {storeData.store_enabled && (
        <>
          {/* Sale Type */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
              Sale Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {["both", "direct_sale", "ppv"].map(type => (
                <button
                  key={type}
                  onClick={() => setStoreData({...storeData, store_sale_type: type})}
                  className={`px-4 py-3 rounded-lg font-medium transition ${storeData.store_sale_type === type ? "" : "opacity-50"}`}
                  style={{
                    background: storeData.store_sale_type === type ? "var(--gio-primary)" : "var(--gio-surface)",
                    color: storeData.store_sale_type === type ? "#fff" : "var(--gio-text)"
                  }}
                >
                  {type === "both" ? "Buy & Rent" : type === "direct_sale" ? "Buy Only" : "Rent Only"}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            {(storeData.store_sale_type === "direct_sale" || storeData.store_sale_type === "both") && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
                  <DollarSign size={14} className="inline mr-1" />
                  Buy Price (Lifetime)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={storeData.store_direct_sale_price}
                  onChange={(e) => setStoreData({...storeData, store_direct_sale_price: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: "var(--gio-bg)",
                    border: "1px solid var(--gio-border)",
                    color: "var(--gio-text)"
                  }}
                />
              </div>
            )}
            
            {(storeData.store_sale_type === "ppv" || storeData.store_sale_type === "both") && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
                  <DollarSign size={14} className="inline mr-1" />
                  Rent Price ({storeData.store_ppv_duration_hours}h)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={storeData.store_ppv_price}
                  onChange={(e) => setStoreData({...storeData, store_ppv_price: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: "var(--gio-bg)",
                    border: "1px solid var(--gio-border)",
                    color: "var(--gio-text)"
                  }}
                />
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
              Store Description (Marketing Copy)
            </label>
            <textarea
              value={storeData.store_description}
              onChange={(e) => setStoreData({...storeData, store_description: e.target.value})}
              placeholder="Exclusive content description for customers..."
              rows={4}
              className="w-full px-4 py-3 rounded-lg resize-y"
              style={{
                background: "var(--gio-bg)",
                border: "1px solid var(--gio-border)",
                color: "var(--gio-text)"
              }}
            />
            <div className="text-xs mt-1" style={{ color: "var(--gio-muted)" }}>
              Tip: Use Promo Pack to generate AI-powered descriptions
            </div>
          </div>

          {/* Trailer & Thumbnail */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
                <Film size={14} className="inline mr-1" />
                Trailer URL (Preview Only)
              </label>
              <input
                type="text"
                value={storeData.trailer_url}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  // Warn if URL contains the main mux_playback_id
                  if (video?.mux_playback_id && newUrl.includes(video.mux_playback_id)) {
                    toast.error("⚠️ WARNING: You're setting the FULL VIDEO as trailer! This exposes premium content for free. Use trailer_mux_playback_id instead.");
                  }
                  setStoreData({...storeData, trailer_url: newUrl});
                }}
                placeholder="https://... (short preview only, not full video)"
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: "var(--gio-bg)",
                  border: "1px solid var(--gio-border)",
                  color: "var(--gio-text)"
                }}
              />
              <div className="flex items-start gap-2 mt-1">
                <button
                  onClick={linkTrailerFromMux}
                  className="text-xs hover:underline"
                  style={{ color: "var(--gio-primary)" }}
                >
                  Link from Mux Trailer
                </button>
                <span className="text-xs" style={{ color: "var(--gio-muted-3)" }}>
                  (Uploads trailer in Mux tab first)
                </span>
              </div>
              {storeData.trailer_url && video?.mux_playback_id && storeData.trailer_url.includes(video.mux_playback_id) && (
                <div className="mt-2 p-2 rounded text-xs bg-red-500/10 border border-red-500/30 text-red-400">
                  🚨 <strong>SECURITY LEAK:</strong> This URL contains your full video! Public users will get free access. Use a separate trailer upload.
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
                <ImageIcon size={14} className="inline mr-1" />
                Store Thumbnail
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleThumbnailUpload(e.target.files[0])}
                    className="hidden"
                    id="store-thumbnail-upload"
                  />
                  <label
                    htmlFor="store-thumbnail-upload"
                    className="flex-1 px-4 py-3 rounded-lg cursor-pointer hover:opacity-80 transition text-center font-medium text-sm"
                    style={{
                      background: "var(--gio-primary)",
                      color: "#fff"
                    }}
                  >
                    {uploadingThumbnail ? "Uploading..." : "📤 Upload Thumbnail"}
                  </label>
                </div>
                <input
                  type="text"
                  value={storeData.thumbnail_url}
                  onChange={(e) => setStoreData({...storeData, thumbnail_url: e.target.value})}
                  placeholder="Or paste thumbnail URL"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: "var(--gio-bg)",
                    border: "1px solid var(--gio-border)",
                    color: "var(--gio-text)"
                  }}
                />
                <div className="text-xs" style={{ color: "var(--gio-muted)" }}>
                  From Pictures tab or upload directly
                </div>
                {storeData.thumbnail_url && (
                  <div className="relative">
                    <img 
                      src={storeData.thumbnail_url} 
                      alt="Thumbnail preview" 
                      className="w-full max-h-48 object-contain rounded-lg border"
                      style={{ borderColor: "var(--gio-border)" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition disabled:opacity-50"
            style={{ background: "var(--gio-primary)", color: "#fff" }}
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Store Settings
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}
